import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useHistory } from "react-router-dom";
import EditIcon from '@mui/icons-material/Edit';
import ApiService from "../../services/api-service";
import "./Admin.scss";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";


export default function Users() {
  const history = useHistory();
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [statusMsg, setStatusMsg] = useState("");
  const [balance, setBalance] = useState(null);
  const [streamingBalance, setStreamingBalance] = useState(null);
  const [addAmount, setAddAmount] = useState("");
  const [dialog, setDialog] = useState({ open: false, action: null });
  const [streamingDialog, setStreamingDialog] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [premiumFilter, setPremiumFilter] = useState("all"); // "all", "premium", "non-premium"
  const [filteredUsers, setFilteredUsers] = useState([]);

  const filterUsers = (userList, filter) => {
    let filtered = userList;
    if (filter === "premium") {
      filtered = userList.filter(user => user.hasPremium === true);
    } else if (filter === "non-premium") {
      filtered = userList.filter(user => user.hasPremium !== true);
    }
    setFilteredUsers(filtered);
  };

  const handleFilterChange = (newFilter) => {
    setPremiumFilter(newFilter);
    filterUsers(users, newFilter);
  };

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    try {
      setStatusMsg("");
      setUsers([]);
      setSelectedUser(null);
      setBalance(null);
      setStreamingBalance(null);
      setLoading(true);

      const keywords = {
        pageNo,
        pageSize: 10, // always 10 results
      ...(search ? { query: search } : {}),
      };

      const response = await ApiService.ADMIN_GET_ALL_USERS(keywords);
      console.log("Searching for:", search, "keywords:", keywords);

    if (response?.result?.response?.length > 0) {
      setUsers(response.result.response);
      filterUsers(response.result.response, premiumFilter);
    } else {
      setUsers([]);
      setFilteredUsers([]);
      setStatusMsg("No users found.");
    }
    } catch (error) {
      setStatusMsg(error?.response?.data?.message || "Failed to search users.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
      console.log("handleVerify called");

  setStatusMsg("");
  if (!selectedUser || !selectedUser._id) {
    setStatusMsg("No user selected.");
    return;
  }
  try {
    await ApiService.ADMIN_VERIFY_USER({
      id: selectedUser._id,
      isVerified: !selectedUser.isVerified, // toggle or set as needed
    });
    setStatusMsg("User verification status updated!");
  } catch (e) {
    setStatusMsg("Failed to verify user.");
  }
  setDialog({ open: false, action: null });
};

  const handleFreeze = async () => {
    setStatusMsg("");
    if (!selectedUser || !selectedUser._id) {
      setStatusMsg("No user selected.");
      return;
    }
    try {
      await ApiService.ADMIN_FREEZE_USER({ userId: selectedUser._id });
      setStatusMsg("User frozen!");
    } catch (e) {
      setStatusMsg("Failed to freeze user.");
    }
    setDialog({ open: false, action: null });
  };

  const handleGetBalance = async () => {
    setStatusMsg("");
    setBalance(null);
    if (!selectedUser || !selectedUser._id) {
      setStatusMsg("No user selected.");
      return;
    }
    try {
      const resp = await ApiService.ADMIN_GET_USER_BALANCE({ userId: selectedUser._id });
      setBalance(resp.result?.balance ?? "N/A");
    } catch (e) {
      setStatusMsg("Failed to get user balance.");
    }
  };

  // Streaming balance handlers
  const handleGetStreamingBalance = async () => {
    setStatusMsg("");
    setStreamingBalance(null);
    if (!selectedUser || !selectedUser._id) {
      setStatusMsg("No user selected.");
      return;
    }
    try {
      const resp = await ApiService.ADMIN_GET_USER_STREAMING_BALANCE(selectedUser._id);
      setStreamingBalance(resp?.result?.balance ?? "N/A");
    } catch (e) {
      setStatusMsg("Failed to get streaming balance.");
    }
  };

  const handleAddStreamingBalance = async () => {
    setStatusMsg("");
    if (!selectedUser || !selectedUser._id) {
      setStatusMsg("No user selected.");
      return;
    }
    if (!addAmount || isNaN(addAmount)) {
      setStatusMsg("Enter a valid amount.");
      return;
    }
    try {
      await ApiService.ADMIN_ADD_USER_STREAMING_BALANCE({
        userId: selectedUser._id,
        amount: Number(addAmount),
      });
      setStatusMsg("Streaming balance updated!");
      setStreamingDialog(false);
      setAddAmount("");
      handleGetStreamingBalance();
    } catch (e) {
      setStatusMsg("Failed to add to streaming balance.");
    }
  };

  return (
    <Box className="adminPortalContainer">
      <Typography className="adminHeader" variant="h4" gutterBottom>
        Users
      </Typography>
      <Box className="tabPanel">
        <Typography className="sectionTitle">Search Users</Typography>
        <Box
          display="flex"
          alignItems="center"
          mb={2}
          component="form"
          onSubmit={handleSearch}
        >
          <TextField
            className="textField"
            label="Search by name, email, or ID"
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
          />
          <Button
            className="actionButton outlined"
            variant="outlined"
            type="submit"
            disabled={loading}
          >
            {loading ? "Searching..." : "Search"}
          </Button>
        </Box>
        
        {/* Premium Filter Buttons */}
        <Box display="flex" gap={2} mb={3}>
          <Button
            variant={premiumFilter === "all" ? "contained" : "outlined"}
            onClick={() => handleFilterChange("all")}
            sx={{ 
              backgroundColor: premiumFilter === "all" ? "#f59e0b" : "transparent",
              color: premiumFilter === "all" ? "#000" : "#f59e0b",
              borderColor: "#f59e0b",
              '&:hover': {
                backgroundColor: premiumFilter === "all" ? "#d97706" : "rgba(245, 158, 11, 0.1)",
                borderColor: "#f59e0b",
              }
            }}
          >
            All Users ({users.length})
          </Button>
          <Button
            variant={premiumFilter === "premium" ? "contained" : "outlined"}
            onClick={() => handleFilterChange("premium")}
            sx={{ 
              backgroundColor: premiumFilter === "premium" ? "#f59e0b" : "transparent",
              color: premiumFilter === "premium" ? "#000" : "#f59e0b",
              borderColor: "#f59e0b",
              '&:hover': {
                backgroundColor: premiumFilter === "premium" ? "#d97706" : "rgba(245, 158, 11, 0.1)",
                borderColor: "#f59e0b",
              }
            }}
          >
            Premium Users ({users.filter(u => u.hasPremium === true).length})
          </Button>
          <Button
            variant={premiumFilter === "non-premium" ? "contained" : "outlined"}
            onClick={() => handleFilterChange("non-premium")}
            sx={{ 
              backgroundColor: premiumFilter === "non-premium" ? "#f59e0b" : "transparent",
              color: premiumFilter === "non-premium" ? "#000" : "#f59e0b",
              borderColor: "#f59e0b",
              '&:hover': {
                backgroundColor: premiumFilter === "non-premium" ? "#d97706" : "rgba(245, 158, 11, 0.1)",
                borderColor: "#f59e0b",
              }
            }}
          >
            Non-Premium ({users.filter(u => u.hasPremium !== true).length})
          </Button>
        </Box>
        <List className="listRoot">
          {filteredUsers.map(user => (
            <ListItem
              button
              key={user._id}
              className={
                selectedUser && selectedUser._id === user._id
                  ? "selectedItem"
                  : "listItem"
              }
              onClick={() => {
                setSelectedUser(user);
                setBalance(null);
                setStreamingBalance(null);
              }}
              selected={selectedUser && selectedUser._id === user._id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <span>{user.name || user.email || user._id}</span>
              {user.hasPremium && (
                <Box
                  sx={{
                    backgroundColor: '#f59e0b',
                    color: '#000',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}
                >
                  PREMIUM
                </Box>
              )}
            </ListItem>
          ))}
        </List>
    {selectedUser && (
  <Box
    className="userDetailCard"
    sx={{
      p: 2,
      mb: 2,
      border: '1px solid #222',
      borderRadius: 2,
      background: '#23272f', // dark background
      color: '#fff',         // light text
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
    }}
  >
    <Box display="flex" alignItems="center" mb={2}>
      <img
        src={selectedUser.profileImg?.filePath || "https://via.placeholder.com/80"}
        alt="Profile"
        style={{
          width: 80,
          height: 80,
          borderRadius: "50%",
          marginRight: 16,
          objectFit: "cover",
          border: "2px solid #444",
          background: "#111"
        }}
      />
      <Box>
        <Typography variant="h6" sx={{ color: "#fff" }}>
          {selectedUser.firstName} {selectedUser.lastName}
        </Typography>
        <Typography variant="body2" sx={{ color: "#b0b0b0" }}>{selectedUser.email}</Typography>
        <Typography variant="body2" sx={{ color: "#b0b0b0" }}>ID: {selectedUser._id}</Typography>
        <Typography variant="body2" sx={{ color: "#b0b0b0" }}>Roles: {selectedUser.roles?.join(", ")}</Typography>
        <Typography variant="body2" sx={{ color: "#b0b0b0" }}>Followers: {selectedUser.followers}</Typography>
        <Typography variant="body2" sx={{ color: "#b0b0b0" }}>Account Balance: {selectedUser.accountBalance}</Typography>
        <Typography variant="body2" sx={{ color: "#b0b0b0" }}>Streaming Balance: {selectedUser.streamingBalance}</Typography>
        <Typography variant="body2" sx={{ color: "#b0b0b0" }}>
          Subscription: {selectedUser.subscriptionStatus} {selectedUser.subscriptionFee ? `($${selectedUser.subscriptionFee})` : ""}
        </Typography>
        <Typography variant="body2" sx={{ color: "#b0b0b0" }}>
          Verified: {selectedUser.isVerified ? "Yes" : "No"}
        </Typography>
        <Typography variant="body2" sx={{ color: "#b0b0b0" }}>
          Online: {selectedUser.isOnline ? "Yes" : "No"}
        </Typography>
        <Typography variant="body2" sx={{ color: selectedUser.hasPremium ? "#f59e0b" : "#b0b0b0", fontWeight: selectedUser.hasPremium ? "bold" : "normal" }}>
          Premium Status: {selectedUser.hasPremium ? "Premium User" : "Standard User"}
        </Typography>
      </Box>
    </Box>
    <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
          <FormControlLabel
        control={
          <Switch
            checked={!!selectedUser.isVerified}
            onChange={() => setDialog({ open: true, action: "verify" })}
            color="primary"
            disabled={loading}
          />
        }
        label={selectedUser.isVerified ? "Verified" : "Not Verified"}
        sx={{ minWidth: 180, color: "#fff" }}
      />
      <Button
        className="actionButton outlined"
        variant="outlined"
        color="error"
        onClick={() => setDialog({ open: true, action: "freeze" })}
        sx={{ minWidth: 140 }}
      >
        Freeze User
      </Button>
      <Button
        className="actionButton outlined"
        variant="outlined"
        color="secondary"
        onClick={handleGetBalance}
        sx={{ minWidth: 140 }}
      >
        Get Balance
      </Button>
      <Button
        className="actionButton outlined"
        variant="outlined"
        color="secondary"
        onClick={handleGetStreamingBalance}
        sx={{ minWidth: 180 }}
      >
        Get Streaming Balance
      </Button>
      <Button
        className="actionButton outlined"
        variant="outlined"
        color="success"
        onClick={() => setStreamingDialog(true)}
        sx={{ minWidth: 200 }}
      >
        Add to Streaming Balance
      </Button>
      <Button
        className="actionButton outlined"
        variant="contained"
        color="primary"
        startIcon={<EditIcon />}
        onClick={() => history.push(`/admin/user/${selectedUser._id}/edit`)}
        sx={{ minWidth: 140 }}
      >
        Edit User
      </Button>
    </Box>
    {balance !== null && (
      <Typography className="statusMsg" sx={{ color: "#fff" }}>Balance: {balance}</Typography>
    )}
    {streamingBalance !== null && (
      <Typography className="statusMsg" sx={{ color: "#fff" }}>
        Streaming Balance: {streamingBalance}
      </Typography>
    )}
  </Box>
)}
        <Typography className="statusMsg" color="primary">
          {statusMsg}
        </Typography>
      </Box>
      {/* Confirmation Dialog */}
      <Dialog
        open={dialog.open}
        onClose={() => setDialog({ open: false, action: null })}
      >
        <DialogTitle>
          {dialog.action === "verify" ? "Verify User" : "Freeze User"}
        </DialogTitle>
        <DialogContent>
          Are you sure you want to {dialog.action} this user?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ open: false, action: null })}>
            Cancel
          </Button>
          <Button
            onClick={dialog.action === "verify" ? handleVerify : handleFreeze}
            color={dialog.action === "verify" ? "primary" : "error"}
            variant="contained"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      {/* Add to Streaming Balance Dialog */}
      <Dialog
        open={streamingDialog}
        onClose={() => setStreamingDialog(false)}
      >
        <DialogTitle>Add to Streaming Balance</DialogTitle>
        <DialogContent>
          <TextField
            label="Amount"
            type="number"
            value={addAmount}
            onChange={e => setAddAmount(e.target.value)}
            fullWidth
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStreamingDialog(false)}>Cancel</Button>
          <Button onClick={handleAddStreamingBalance} variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}